import type { ProxyReplayFixtureSet } from "../proxyRegistration";
import type {
  BodyRef,
  HeaderPair,
  RequestLogRecord,
} from "../requestLogRecord";
import { bytesAreUtf8Text } from "./volumes";

/** Per-test sidecar holding recorded external-request fixtures for replay. */
export const STEP_TEST_REQUESTS_SUFFIX = ".requests";

// Magic host the runner's replay control calls target; mirrored as the literal
// intercepted in services/proxy/src/transparent.rs before the deny-egress check.
export const STEP_TEST_CONTROL_HOST = "step-tests.3b.internal";

export const MAX_STEP_TEST_REQUEST_COUNT = 64;

/** Max total size of a `.requests` fixture file — the one place `MAX_STEP_TEST_BYTES` is exceeded. */
export const MAX_STEP_TEST_REQUESTS_BYTES = 1_048_576;

export type ClassifiedRequestLogEntry =
  | {
      replayable: true;
      method: string;
      url: string;
      reqHeaders: HeaderPair[];
      reqBody: Buffer;
      status: number;
      resHeaders: HeaderPair[];
      resBody: Buffer;
    }
  | {
      replayable: false;
      method: string;
      url: string;
      reason: string;
    };

export type ParsedRequestsBlob = {
  entries: ClassifiedRequestLogEntry[];
  dropped: number;
};

// Blobs recorded before the proxy stamped `internal` may still contain
// platform-internal lines, so the known internal hosts are skipped by URL too.
function hasInternalHost(url: string): boolean {
  let host: string;
  try {
    host = new URL(url).hostname;
  } catch {
    return false;
  }
  return host === STEP_TEST_CONTROL_HOST || host.endsWith(".runtime.3b");
}

function sliceBody(bodies: Buffer, ref: BodyRef | undefined): Buffer {
  if (!ref) return Buffer.alloc(0);
  const [offset, length] = ref;
  return bodies.subarray(offset, offset + length);
}

/**
 * Classifies a recorded-requests transcript into replayable exchanges, each
 * paired with a reason when it can't be replayed. Platform-internal lines are
 * skipped (proxy plumbing, not step-authored requests), and any
 * `{"dropped":N}` sentinel from the worker's persistence cap is counted but
 * produces no entry.
 */
export function parseRecordedRequestsBlob(
  index: string,
  bodies: Buffer | undefined
): ParsedRequestsBlob {
  const body = bodies ?? Buffer.alloc(0);
  const entries: ClassifiedRequestLogEntry[] = [];
  let dropped = 0;
  for (const line of index.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    let record: RequestLogRecord & { dropped?: number };
    try {
      record = JSON.parse(trimmed);
    } catch {
      continue;
    }
    if (typeof record !== "object" || record === null) continue;
    if (typeof record.dropped === "number") {
      dropped += record.dropped;
      continue;
    }
    if (typeof record.method !== "string" || typeof record.url !== "string") {
      continue;
    }
    const { method, url } = record;
    if (record.internal === true || hasInternalHost(url)) continue;
    const named = (reason: string) => `${method} ${url}: ${reason}`;
    if (record.rec !== 1) {
      entries.push({
        replayable: false,
        method,
        url,
        reason: named(
          record.rec_skipped === "budget"
            ? "skipped — recording budget exceeded"
            : "not recorded (websocket or metadata-only)"
        ),
      });
      continue;
    }
    if (record.req_body_truncated) {
      entries.push({
        replayable: false,
        method,
        url,
        reason: named("request body was truncated"),
      });
      continue;
    }
    if (record.res_body_truncated) {
      entries.push({
        replayable: false,
        method,
        url,
        reason: named("response body was truncated"),
      });
      continue;
    }
    if (record.res_body_complete !== true) {
      entries.push({
        replayable: false,
        method,
        url,
        reason: named("response body was incomplete (connection aborted)"),
      });
      continue;
    }
    entries.push({
      replayable: true,
      method,
      url,
      reqHeaders: record.req_headers ?? [],
      reqBody: sliceBody(body, record.req_body_ref),
      status: record.status ?? 0,
      resHeaders: record.res_headers ?? [],
      resBody: sliceBody(body, record.res_body_ref),
    });
  }
  return { entries, dropped };
}

export type StepTestRequestFixture = {
  method: string;
  url: string;
  reqHeaders: HeaderPair[];
  reqBody: Buffer;
  status: number;
  resHeaders: HeaderPair[];
  resBody: Buffer;
  /** Serializer match key (`tests/serialize-request`), when the step authors one. */
  key?: string;
};

type StepTestRequestFixtureLine = {
  v: 1;
  method: string;
  url: string;
  // Optional: fixtures committed before request headers were captured have none.
  reqHeaders?: HeaderPair[];
  reqBody: string;
  reqBodyEncoding: "utf8" | "base64";
  status: number;
  resHeaders: HeaderPair[];
  resBody: string;
  resBodyEncoding: "utf8" | "base64";
  key?: string;
};

function encodeFixtureBody(bytes: Buffer): {
  content: string;
  encoding: "utf8" | "base64";
} {
  if (bytesAreUtf8Text(bytes)) {
    return { content: bytes.toString("utf-8"), encoding: "utf8" };
  }
  return { content: bytes.toString("base64"), encoding: "base64" };
}

function decodeFixtureBody(
  content: string,
  encoding: "utf8" | "base64"
): Buffer {
  return Buffer.from(content, encoding === "base64" ? "base64" : "utf-8");
}

/** Serialize recorded exchanges to the `.requests` commit file's JSONL format. */
export function serializeRequestFixtures(
  entries: readonly StepTestRequestFixture[]
): string {
  return entries
    .map((entry) => {
      const req = encodeFixtureBody(entry.reqBody);
      const res = encodeFixtureBody(entry.resBody);
      const line: StepTestRequestFixtureLine = {
        v: 1,
        method: entry.method,
        url: entry.url,
        reqHeaders: entry.reqHeaders,
        reqBody: req.content,
        reqBodyEncoding: req.encoding,
        status: entry.status,
        resHeaders: entry.resHeaders,
        resBody: res.content,
        resBodyEncoding: res.encoding,
        ...(entry.key !== undefined ? { key: entry.key } : {}),
      };
      return JSON.stringify(line);
    })
    .join("\n");
}

export function parseRequestFixtures(
  content: string
): StepTestRequestFixture[] {
  const entries: StepTestRequestFixture[] = [];
  for (const line of content.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const parsed = JSON.parse(trimmed) as StepTestRequestFixtureLine;
    entries.push({
      method: parsed.method,
      url: parsed.url,
      reqHeaders: parsed.reqHeaders ?? [],
      reqBody: decodeFixtureBody(parsed.reqBody, parsed.reqBodyEncoding),
      status: parsed.status,
      resHeaders: parsed.resHeaders,
      resBody: decodeFixtureBody(parsed.resBody, parsed.resBodyEncoding),
      ...(parsed.key !== undefined ? { key: parsed.key } : {}),
    });
  }
  return entries;
}

/** Validate recorded exchanges against the fixture count/size caps and shape. */
export function validateRequestFixtures(
  entries: readonly StepTestRequestFixture[]
): string | null {
  if (entries.length > MAX_STEP_TEST_REQUEST_COUNT) {
    return `Step tests may record at most ${MAX_STEP_TEST_REQUEST_COUNT} requests`;
  }
  for (const entry of entries) {
    if (!entry.method || !entry.url) {
      return "Recorded requests must have a method and URL";
    }
    if (!Number.isInteger(entry.status)) {
      return "Recorded requests must have an integer status";
    }
  }
  const serialized = serializeRequestFixtures(entries);
  if (Buffer.byteLength(serialized, "utf-8") > MAX_STEP_TEST_REQUESTS_BYTES) {
    return "Recorded requests must total under 1 MB";
  }
  return null;
}

/** Shape Pony’s egress-context registration expects for a fixture set. */
export function requestFixturesToProxyWire(
  name: string,
  entries: readonly StepTestRequestFixture[]
): ProxyReplayFixtureSet {
  return {
    name,
    requests: entries.map((entry) => ({
      method: entry.method,
      url: entry.url,
      reqBodyB64: entry.reqBody.toString("base64"),
      status: entry.status,
      resHeaders: entry.resHeaders,
      resBodyB64: entry.resBody.toString("base64"),
      ...(entry.key !== undefined ? { key: entry.key } : {}),
    })),
  };
}

/**
 * Merges the capture run's per-fixture serializer keys (in recorded order)
 * into the extracted `.requests` fixtures before they're persisted, so a
 * step with `tests/serialize-request` gets keyed fixtures from the moment
 * they're first pinned.
 */
export function applyCapturedRequestFixtureKeys(
  requestFixtures: string | undefined,
  keys: readonly string[] | undefined
): string | undefined {
  if (requestFixtures === undefined || keys === undefined) {
    return requestFixtures;
  }
  return mergeRequestFixtureKeys(requestFixtures, keys);
}

function mergeRequestFixtureKeys(
  serializedFixtures: string,
  keys: readonly string[]
): string {
  const fixtures = parseRequestFixtures(serializedFixtures);
  if (keys.length !== fixtures.length) {
    throw new Error(
      "Recorded request fixture count doesn’t match the computed serializer key count"
    );
  }
  return serializeRequestFixtures(
    fixtures.map((fixture, i) => ({ ...fixture, key: keys[i] }))
  );
}
